/*
 * The legacy bulk-update grid, parsed in the browser.
 *
 * V1's slot-settings page has a textarea that sets DIFFERENT values on DIFFERENT slots in one go -
 * the one thing "copy one slot onto many" cannot express, and the only practical way to hand a
 * hundred slots a hundred different Instagram logins. This is that format:
 *
 *     anything,  1,        8,       51-53
 *     slot1,     My Label, pass1,   on
 *     slot5-10,  Other,    pass2,   off
 *
 *   - Row 0 is the HEADER: cell 0 is a placeholder (V1 overwrites it and never reads it), and
 *     every cell after it is a setting id, or a range of ids.
 *   - Every later row is one target: cell 0 is a slot (`slot5`, `5`, `slot5-10`, `5-10`), and the
 *     cells after it line up with the header ids.
 *   - `thisSlot` anywhere in the text is replaced with the source slot's name, as in V1.
 *
 * Deliberate differences from V1's parser (SlotServiceImpl.bulkSave, ~331 lines):
 *   - Header ranges expand IN PLACE rather than being appended to the end. V1's append works
 *     because it repeats each row's value in the same order, but in-place expansion is what the
 *     column layout looks like it means, and it survives more than one range in a header.
 *   - Everything is validated and REPORTED before anything is sent, instead of failing partway
 *     through a save. Nothing here writes; the caller feeds the result to the ordinary
 *     PUT /uiapi/slots/{name}/settings path, which is what keeps per-slot undo working.
 *
 * Known limit, shared with V1: cells are comma-separated, so a value containing a comma cannot be
 * expressed. That is reported as a parse error rather than silently mangling the row.
 */

const RANGE = /^(?:slot)?\s*(\d+)\s*-\s*(?:slot)?\s*(\d+)$/i;
const SINGLE = /^(?:slot)?\s*(\d+)$/i;

/** `slot5-10`, `5-10`, `slot7`, `7` -> ['slot5'...'slot10'] | null when it is not a slot spec. */
function expandSlotSpec(cell) {
  const t = String(cell || '').trim().replace(/[‒–—−]/g, '-');
  const r = t.match(RANGE);
  if (r) {
    let a = parseInt(r[1], 10);
    let b = parseInt(r[2], 10);
    if (a > b) { const s = a; a = b; b = s; }
    if (b - a > 100000) return null;
    const out = [];
    for (let n = a; n <= b; n++) out.push(`slot${n}`);
    return out;
  }
  const s = t.match(SINGLE);
  return s ? [`slot${parseInt(s[1], 10)}`] : null;
}

/** A header cell -> the ids it covers. Returns null when it is not readable as an id or range. */
function expandIdSpec(cell) {
  const t = String(cell || '').trim().replace(/[‒–—−]/g, '-');
  const r = t.match(/^(\d+)\s*-\s*(\d+)$/);
  if (r) {
    let a = parseInt(r[1], 10);
    let b = parseInt(r[2], 10);
    if (a > b) { const s = a; a = b; b = s; }
    if (b - a > 100000) return null;
    const out = [];
    for (let n = a; n <= b; n++) out.push(n);
    return out;
  }
  return /^\d+$/.test(t) ? [parseInt(t, 10)] : null;
}

/**
 * @param text        the raw textarea contents
 * @param opts.sourceName  slot name that `thisSlot` stands for (optional)
 * @param opts.knownIds    Set of setting ids the server has; omit to skip id validation
 * @param opts.ownedNames  slot names the account owns; omit to skip slot validation
 * @returns {{rows: {slot: string, values: Object}[], ids: number[], errors: string[],
 *            warnings: string[], slotCount: number, valueCount: number}}
 *          `rows` is empty whenever `errors` is non-empty - a grid is all-or-nothing to read,
 *          because a row that silently dropped out is a slot that silently did not get set.
 */
export function parseSettingsGrid(text, opts) {
  const o = opts || {};
  const errors = [];
  const warnings = [];
  const empty = { rows: [], ids: [], errors, warnings, slotCount: 0, valueCount: 0 };

  let raw = String(text == null ? '' : text);
  if (o.sourceName) raw = raw.split('thisSlot').join(o.sourceName);

  const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return empty;
  if (lines.length === 1) {
    errors.push('Only a header row - add at least one slot row underneath it.');
    return empty;
  }

  // ---- header: cell 0 is a placeholder, the rest are setting ids -------------------------
  const headerCells = lines[0].split(',').map((c) => c.trim());
  if (headerCells.length < 2) {
    errors.push('The first row must list the setting ids, e.g.  slot, 1, 8, 51-53 '
      + '(a list of slots with no ids is an ordinary copy - use "Everything" instead).');
    return empty;
  }
  const ids = [];
  for (let j = 1; j < headerCells.length; j++) {
    const expanded = expandIdSpec(headerCells[j]);
    if (!expanded) {
      errors.push(`Header column ${j}: "${headerCells[j]}" is not a setting id or range.`);
      continue;
    }
    // Each header cell contributes a COLUMN GROUP; one typed value fills every id in its range.
    ids.push(expanded);
  }
  if (errors.length) return empty;

  const flatIds = [].concat.apply([], ids);
  if (o.knownIds) {
    const missing = flatIds.filter((n) => !o.knownIds.has(n));
    const uniqueMissing = Array.from(new Set(missing));
    if (uniqueMissing.length) {
      errors.push(uniqueMissing.length <= 12
        ? `No setting numbered ${uniqueMissing.join(', ')}.`
        : `${uniqueMissing.length} of those setting numbers do not exist on this server.`);
      return empty;
    }
  }

  // ---- data rows ------------------------------------------------------------------------
  const bySlot = new Map();
  const unknownSlots = new Set();
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(',').map((c) => c.trim());
    const slots = expandSlotSpec(cells[0]);
    if (!slots) {
      errors.push(`Row ${i + 1}: "${cells[0]}" is not a slot or slot range.`);
      continue;
    }
    if (cells.length - 1 > ids.length) {
      errors.push(`Row ${i + 1} has ${cells.length - 1} values but the header lists ${ids.length}. `
        + 'A value containing a comma cannot be expressed in this grid.');
      continue;
    }
    if (cells.length - 1 < ids.length) {
      warnings.push(`Row ${i + 1} sets only the first ${cells.length - 1} of ${ids.length} columns; `
        + 'the rest are left alone.');
    }

    slots.forEach((name) => {
      if (o.ownedNames && !o.ownedNames.has(name)) { unknownSlots.add(name); return; }
      const values = bySlot.get(name) || {};
      for (let j = 1; j < cells.length; j++) {
        // One typed cell fills every id in that header group (a `51-53` column).
        ids[j - 1].forEach((id) => { values[String(id)] = cells[j]; });
      }
      bySlot.set(name, values);
    });
  }
  if (errors.length) return empty;

  if (unknownSlots.size) {
    const list = Array.from(unknownSlots);
    warnings.push(list.length <= 12
      ? `Not on this account, skipped: ${list.join(', ')}.`
      : `${list.length} slots are not on this account and were skipped.`);
  }

  const rows = Array.from(bySlot.entries())
    .map(([slot, values]) => ({ slot, values }))
    .sort((a, b) => (parseInt(a.slot.replace(/\D/g, ''), 10) || 0)
      - (parseInt(b.slot.replace(/\D/g, ''), 10) || 0));

  if (rows.length === 0) {
    errors.push('No slot in this grid is on your account.');
    return { rows: [], ids: flatIds, errors, warnings, slotCount: 0, valueCount: 0 };
  }

  return {
    rows,
    ids: flatIds,
    errors,
    warnings,
    slotCount: rows.length,
    valueCount: rows.reduce((n, r) => n + Object.keys(r.values).length, 0),
  };
}
